import React, { useEffect, useState } from 'react';
import {
  Page,
  Text,
  Link,
  View,
  Document,
  Svg,
  Path,
  Image,
  StyleSheet,
  PDFDownloadLink,
  Font
} from '@react-pdf/renderer';
import styled from 'styled-components';
import { downloadLink } from '~/App/helpers/mixins';
import { IMeCollection } from '~/types/ICollection';
import QRCode from 'qrcode';
import mq from '~/App/helpers/mq';

import CFFont from '../../../../../assets/fonts/CancerfondenSans/CancerfondenSans-Regular.woff';
import CFFontBold from '../../../../../assets/fonts/CancerfondenSans/CancerfondenSans-Bold.woff';

const CreatePdfButton = styled.button`
  color: ${({ theme }) => theme.themeColors.primary};
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: 1.125rem;
  transition: color 0.3s ease-in-out;
  text-decoration: none;
  cursor: pointer;
  border: 0;
  background: none;
  outline: 0;
  margin-top: 0;
  padding: 0;

  ${mq('≥small')`
    font-size: 1.125rem;
    line-height: 1.25rem;
  `};

  &:hover {
    color: ${({ theme }) => theme.themeColors.primaryHover};
  }
`;

const DownloadPdfButton = styled.button`
  ${downloadLink};
  color: ${({ theme }) => theme.themeColors.primary};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  transition: color 0.3s ease-in-out;
  text-decoration: none;
  cursor: pointer;
  border: 0;
  background: none;
  outline: 0;
  margin-top: 0;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.themeColors.primaryHover};
  }
`;

Font.register({
  family: 'CancerfondenSans',
  fonts: [
    { src: CFFont, fontWeight: 400 },
    { src: CFFontBold, fontWeight: 700 }
  ]
});

Font.registerHyphenationCallback((word) => {
  // Return entire word as unique part
  return [word];
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    fontFamily: 'CancerfondenSans',
    color: '#4A4A4A'
  },
  topSection: {
    marginTop: 55,
    paddingBottom: 42,
    alignItems: 'center',
    flexGrow: 1,
    width: 450,
    textAlign: 'center',
    fontSize: 18
  },
  title: {
    maxLines: 2,
    marginTop: 32,
    marginBottom: 16,
    fontSize: 22,
    fontWeight: 700,
    maxWidth: 225
  },
  topSectionText: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  image: {
    width: 200,
    height: 200,
    borderRadius: 10
  },
  qrBlock: {
    backgroundColor: '#F1F1F1',
    height: 206,
    width: 600,
    paddingVertical: 30,
    justifyContent: 'center',
    alignItems: 'center'
  },
  qrWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: 50
  },
  qrImage: {
    height: 150,
    width: 150,
    marginRight: 20
  },
  qrText: {
    width: 250,
    display: 'flex',
    flexDirection: 'row',
    fontWeight: 700
  },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'center',
    height: 123,
    bottom: 10,
    paddingHorizontal: 25,
    width: 600
  },
  logoWrapper: {
    justifyContent: 'center',
    height: 70,
    flex: 1
  },
  footerText: {
    fontSize: 14,
    flex: 1
  }
});

// Create Document Component

type DocProps = {
  imageUrl: string | undefined;
  title: string;
  qr: string;
  owner: string | undefined;
};

const MyDocument = (collection: DocProps) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.topSection}>
        <Image src={collection.imageUrl} style={styles.image} />
        <Text style={styles.title}>{collection.title}</Text>
        <Text style={styles.topSectionText}>
          Den här insamlingen har startats hos Cancerfonden av{' '}
          {collection.owner}. Pengarna går till Sveriges främsta cancerforskning
          och målet är att färre ska drabbas och fler överleva sin sjukdom.
        </Text>
      </View>
      <View style={styles.qrBlock}>
        <View style={styles.qrWrapper}>
          <Image src={collection.qr} style={styles.qrImage}></Image>
          <Text style={styles.qrText}>
            Stöd insamlingen: Scanna QR-koden och ge en livsviktig gåva!
          </Text>
        </View>
      </View>
      <View style={styles.footer}>
        <View style={styles.footerText}>
          <Text>#tillsammansmotcancer</Text>
          <Text>
            www.cancerfonden.se <Link src={'/insamling'}></Link>
          </Text>
        </View>
        <View style={styles.logoWrapper}>
          <Svg viewBox="0 0 1181.4 636.5">
            <Path
              d="M755.8 227.1c-4.8-6.6-10.5-12.8-17.1-18.4a105.09 105.09 0 0 0-52.6-23.9 32.84 32.84 0 0 1-25.3-19.2c-3.5-8-7.9-15.6-13.2-22.8-8.9-12.1-20.1-23-33.6-31.8-48.3-31.6-113.4-25.9-155.5 13.6a126.99 126.99 0 0 0-16.2 167.8c5.8 8 12.7 15.4 20.5 22 29.1 24.7 66.2 34 101.2 28.7 8-1.2 16.1 1.9 20.8 8.4a106 106 0 0 0 26.8 25.7c40.5 27.2 95.7 22.6 131.1-11 40.2-37.8 43.5-97.5 13.1-139.1zm-50.2 92.2c-8 9.4-19.2 15.2-31.5 16.2s-24.3-2.8-33.7-10.8c-2.8-2.4-5.3-5.1-7.5-8a46.16 46.16 0 0 1 2.2-57.2c16.5-19.5 45.8-21.8 65.2-5.3 2.8 2.4 5.3 5.1 7.5 8a46.03 46.03 0 0 1-2.2 57.1zm-115.9-98.4c-18 21.2-26.1 47.4-24.9 73.1.4 8-6 14.8-14 15.3a91.47 91.47 0 0 1-65.3-21.5 90.4 90.4 0 0 1-14.9-16 91.58 91.58 0 0 1 4.3-113.6c32.8-38.6 90.9-43.4 129.5-10.6a90.4 90.4 0 0 1 14.9 16c2.9 4 5.5 8.2 7.7 12.5 3.7 7.2.8 16.1-6.3 19.9a105.1 105.1 0 0 0-31 24.9zM148.4 485c-3.2-8.3-9.3-13.4-17.5-13.4-13.2 0-20.3 13.2-20.3 28.9 0 15.9 7.1 28.9 20.4 28.9 8.5 0 15-5.4 18.1-13.6l19.3 4.2c-5.4 18.4-18.8 26.4-36.9 26.4-26.8 0-40.9-17.7-40.9-45.9 0-28.4 14.2-45.9 40.8-45.9 18.1 0 31.1 8.2 36.4 26.3l-19.4 4.1zM229.7 526.1h-31.9l-6.4 19.1h-19.9l29.4-86.2 24.5-4.4 30.5 90.7h-20l-6.2-19.2zm-5.7-17.3l-10.1-31.2-10.5 31.1 20.6.1zM343.1 456.2l-.1 89h-22.3L288.1 489v56.2h-19.6l.1-89h22.3l32.5 56.4v-56.3l19.7-.1zM414.5 485.2c-3.2-8.3-9.3-13.4-17.5-13.4-13.2 0-20.3 13.2-20.3 28.9 0 15.9 7.1 28.9 20.4 28.9 8.5 0 15-5.4 18.1-13.6l19.3 4.2c-5.4 18.4-18.8 26.4-36.9 26.4-26.8 0-40.9-17.7-40.9-45.9 0-28.4 14.2-45.9 40.8-45.9 18.1 0 31.1 8.2 36.4 26.3l-19.4 4.1zM468.1 492.9h28.7l-3.9 16.4h-24.8v19.6h38.4v16.4l-58.3-.1.1-89 58.3.1-3.9 16.4h-34.5l-.1 20.2zM574.3 510l14.9 35.5h-21.1L556.2 515h-12.5v30.5h-20l.1-89h31.7c17.9 0 32.7 9.9 32.8 28.3-.1 11.6-5.8 20.2-14 25.2zm-18.8-11.4c5.8 0 14.6-3.2 14.2-13.5-.3-8.5-7.8-12.2-14.3-12.3h-11.7v25.7l11.8.1zM625.7 493.1h28.7l-3.9 16.4h-24.8v36h-19.9l.1-89 58.3.1-3.9 16.4h-34.5l-.1 20.1zM667.4 501c0-28.2 14.9-45.9 41.8-45.9 26.8 0 41.7 17.7 41.7 45.9s-14.9 45.9-41.8 45.9-41.7-17.6-41.7-45.9zm63.6.1c0-15.9-7.6-28.9-21.8-29-14.2 0-21.9 13.1-21.9 28.9 0 15.9 7.6 28.9 21.8 29 14.2 0 21.9-13.1 21.9-28.9zM839.2 456.7l-.1 89h-22.3l-32.6-56.2v56.2h-19.6l.1-89H787l32.5 56.4v-56.3l19.7-.1zM883.2 456.7c21.4 0 43.5 11.2 43.5 44.5s-22.3 44.5-43.6 44.5h-26.8l.1-89h26.8zm0 72.6c9 0 24.9-3.6 24.9-27.5 0-23.8-15.6-28.7-24.9-28.7h-7v56.2h7zM960.5 493.4h28.7l-3.9 16.4h-24.8v19.6h38.4v16.4l-58.3-.1.1-89 58.3.1-3.9 16.4h-34.5l-.1 20.2zM1090.7 456.9l-.1 89h-22.3l-32.6-56.2v56.2h-19.6l.1-89h22.3l32.5 56.4V457l19.7-.1z"
              fill="#001489"
            />
          </Svg>
        </View>
        <View style={styles.logoWrapper}>
          <Image src="https://res.cloudinary.com/cancerfonden/image/upload/v1666358354/assets/90_Logo_version_red.png"></Image>
        </View>
      </View>
    </Page>
  </Document>
);

type Props = {
  data: IMeCollection;
};

export const CreatePDF = ({ data }: Props) => {
  const [isClient, setIsClient] = useState(false);
  const [pdfRequested, setPdfRequested] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const url = data.share?.url ? data.share.url : '';

  useEffect(() => {
    setIsClient(true);
  }, []);

  const generatePdf = () => {
    generateQRCode();
    setPdfRequested(true);
  };

  const generateQRCode = () => {
    QRCode.toDataURL(
      url,
      {
        width: 800,
        margin: 2
      },
      (err: any, url: string) => {
        if (err) return console.error(err);
        setQrCode(url);
      }
    );
  };

  return (
    <>
      {!pdfRequested && (
        <CreatePdfButton onClick={() => generatePdf()} children="Skapa PDF" />
      )}

      {isClient && pdfRequested && (
        <PDFDownloadLink
          document={
            <MyDocument
              imageUrl={data.imageUrl}
              title={data.name}
              qr={qrCode}
              owner={data.senderName}
            />
          }
          fileName={`${data.slug}.pdf`}
          style={{}}
        >
          {({ blob, url, loading, error }) =>
            loading ? (
              <CreatePdfButton>Laddar affisch...</CreatePdfButton>
            ) : (
              <DownloadPdfButton children="Ladda ned affisch" />
            )
          }
        </PDFDownloadLink>
      )}
    </>
  );
};
